import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertCircle, Check, ChevronDown, Database, RefreshCw, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isChatbotOverrideEnabled } from "@/lib/chatbotBaseUrl";
import {
  applyEnvironment,
  clearEnvironmentOverrides,
  environmentBlocker,
  pairingMismatch,
  type ChatbotTarget,
  type DatabaseTarget,
  type Environment,
} from "@/lib/sandboxEnvironments";
import {
  activeBranchLabel,
  buildLine,
  chatbotHostLabel,
  chatbotLabel,
  environmentFacts,
  errorText,
  isCurrentEnvironment,
  pairingVerdict,
  selectedChatbot,
  selectedDatabase,
  useChatbotPairing,
  useEnvironments,
  type EnvironmentsQuery,
  type PairingView,
  type Verdict,
} from "@/components/dev/environmentPicker";

/**
 * Dev-only picker for the whole sandbox environment — database AND chatbot.
 *
 * A preview build bakes in one Supabase project and one chatbot URL, and the two
 * are configured independently, so nothing stops them pointing at different
 * databases: the chatbot then rejects the app's access token, or worse answers
 * from a different database than the UI is showing. So the two are picked on two
 * axes — one row per DATABASE, and a chatbot selector on the row you have selected,
 * any chatbot with any database — and applied together, then reloaded; both
 * overrides are read at module init, exactly like the Supabase branch override
 * always has been. There is deliberately no way to enter a chatbot by hand: an
 * arbitrary URL is the one control that could move it without its database.
 *
 * Gated on VITE_ENABLE_BRANCH_SWITCHER, so a production build never renders it and
 * never honours an override written by hand.
 */
export function EnvironmentSwitcher() {
  const location = useLocation();
  const enabled = isChatbotOverrideEnabled();
  const [isOpen, setIsOpen] = useState(false);
  const envs = useEnvironments(enabled);
  const pairing = useChatbotPairing(enabled);

  if (!enabled) return null;

  // Yield to the tool screens' top-centre floating controls.
  if (location.pathname === "/tools" || location.pathname.endsWith("/drawings")) {
    return null;
  }

  return (
    <div className="fixed left-1/2 top-4 z-40 -translate-x-1/2">
      <SwitcherTrigger
        broken={pairing.verdict.broken}
        isLoading={envs.isLoading}
        onOpen={() => setIsOpen(true)}
      />

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-xl">
          <PickerDialog envs={envs} pairing={pairing} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SwitcherTrigger({
  broken,
  isLoading,
  onOpen,
}: {
  broken: boolean;
  isLoading: boolean;
  onOpen: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="control"
      onClick={onOpen}
      className={`min-w-[240px] justify-between ${broken ? "border-destructive text-destructive" : ""}`}
    >
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4" />
        <span className="truncate">{activeBranchLabel()}</span>
      </div>
      <TriggerIcon broken={broken} isLoading={isLoading} />
    </Button>
  );
}

function TriggerIcon({ broken, isLoading }: { broken: boolean; isLoading: boolean }) {
  if (broken) return <TriangleAlert className="h-4 w-4 text-destructive" />;
  if (isLoading) return <RefreshCw className="h-4 w-4 animate-spin" />;
  return <ChevronDown className="h-4 w-4" />;
}

function PickerDialog({ envs, pairing }: { envs: EnvironmentsQuery; pairing: PairingView }) {
  const [actionError, setActionError] = useState<string | null>(null);
  // Both choices are stored as "what the user picked, if anything" and resolved against
  // the current lists on render, so a Refresh that drops a branch or a sandbox chatbot
  // cannot leave either control pointing at something that is no longer offered.
  const [databaseKey, setDatabaseKey] = useState<string | null>(null);
  const [chatbotUrl, setChatbotUrl] = useState<string | null>(null);

  const chatbot = selectedChatbot(envs.chatbots, chatbotUrl);
  const database = selectedDatabase(envs.databases, databaseKey, pairing.appRef);

  /** Every action here rewrites an override, so the page has to reload to pick it up. */
  const apply = (write: () => void) => {
    try {
      write();
      window.location.reload();
    } catch (cause) {
      setActionError(errorText(cause));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Sandbox environment</DialogTitle>
        <DialogDescription>
          One row per database. Pick one, then the chatbot it should use — any combination — and
          switching points the app at both and reloads.
        </DialogDescription>
      </DialogHeader>

      {/* The pairing is the thing that silently breaks, so it goes first. */}
      <PairingCard pairing={pairing} />
      <PickerErrors actionError={actionError} envs={envs} />

      <DatabaseList
        envs={envs}
        app={pairing}
        chatbot={chatbot}
        selectedKey={database ? database.key : null}
        onSelectDatabase={setDatabaseKey}
        onSelectChatbot={setChatbotUrl}
        onApply={(env) => apply(() => applyEnvironment(env))}
      />

      <DialogFooter className="gap-2 sm:justify-between">
        <Button variant="ghost" onClick={() => apply(clearEnvironmentOverrides)}>
          Reset to build defaults
        </Button>
        <Button variant="outline" onClick={envs.refresh} disabled={envs.isLoading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${envs.isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </DialogFooter>
    </>
  );
}

function PickerErrors({
  actionError,
  envs,
}: {
  actionError: string | null;
  envs: EnvironmentsQuery;
}) {
  return (
    <>
      {actionError && <p className="text-sm text-destructive">{actionError}</p>}
      {envs.branchError && (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" /> Branch list unavailable ({envs.branchError})
        </p>
      )}
      {envs.sandboxError && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <AlertCircle className="h-4 w-4" /> No sandbox chatbots listed ({envs.sandboxError}) — the
          develop chatbot is the only choice, which is what every preview build uses anyway.
        </p>
      )}
    </>
  );
}

function PairingCard({ pairing }: { pairing: PairingView }) {
  const { verdict, health, appRef, chatbotUrl } = pairing;
  const build = buildLine(health);

  return (
    <div
      className={`rounded-md border p-3 text-sm ${verdict.broken ? "border-destructive bg-destructive/5" : ""}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">App database</span>
        <span className="font-mono text-xs text-muted-foreground">{appRef || "unknown"}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">Chatbot</span>
        <span className="font-mono text-xs text-muted-foreground">
          {chatbotHostLabel(chatbotUrl)}
        </span>
      </div>

      <VerdictLine verdict={verdict} />

      {build && <p className="mt-2 text-xs text-muted-foreground">{build}</p>}
    </div>
  );
}

function VerdictLine({ verdict }: { verdict: Verdict }) {
  const Icon = verdict.icon;
  return (
    <p className={`mt-2 flex items-start gap-1.5 ${verdict.className}`}>
      <Icon
        className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${verdict.spin ? "animate-spin" : ""}`}
        aria-hidden
      />
      <span>{verdict.text}</span>
    </p>
  );
}

/**
 * How this build is wired right now — the two facts every row judges itself
 * against: which database the app is actually on, and whether the chatbot
 * follows the token. Taken as one prop because they always travel together,
 * from the pairing down to the row's Switch button.
 */
type AppWiring = Pick<PairingView, "appRef" | "routesPerToken">;

/**
 * The databases, one row each. The chatbot is not a row and never splits one: the row
 * the user has selected carries the chatbot selector, and every other row shows the
 * chatbot it would be paired with if selected.
 */
function DatabaseList({
  envs,
  app,
  chatbot,
  selectedKey,
  onSelectDatabase,
  onSelectChatbot,
  onApply,
}: {
  envs: EnvironmentsQuery;
  app: AppWiring;
  chatbot: ChatbotTarget;
  selectedKey: string | null;
  onSelectDatabase: (key: string) => void;
  onSelectChatbot: (url: string) => void;
  onApply: (env: Environment) => void;
}) {
  return (
    <div className="max-h-72 space-y-1 overflow-y-auto">
      {envs.databases.length === 0 && (
        <p className="px-1 py-4 text-sm text-muted-foreground">
          {envs.isLoading ? "Loading environments…" : "No environments available"}
        </p>
      )}

      {envs.databases.map((database) => (
        <DatabaseRow
          key={database.key}
          env={{ ...database, chatbot }}
          app={app}
          chatbots={envs.chatbots}
          selected={database.key === selectedKey}
          onSelect={() => onSelectDatabase(database.key)}
          onSelectChatbot={onSelectChatbot}
          onApply={onApply}
        />
      ))}
    </div>
  );
}

function DatabaseRow({
  env,
  app,
  chatbots,
  selected,
  onSelect,
  onSelectChatbot,
  onApply,
}: {
  env: Environment;
  app: AppWiring;
  chatbots: ChatbotTarget[];
  selected: boolean;
  onSelect: () => void;
  onSelectChatbot: (url: string) => void;
  onApply: (env: Environment) => void;
}) {
  return (
    <div className={`rounded-md border p-2 text-sm ${selected ? "border-primary" : ""}`}>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="w-full text-left hover:opacity-80"
      >
        <DatabaseRowHeader env={env} current={isCurrentEnvironment(env, app.appRef)} />

        <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          {environmentFacts(env).map((fact) => (
            <span key={fact.label} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 shrink-0 rounded-full ${fact.dotClass}`} />
              <span className="truncate">{fact.label}</span>
            </span>
          ))}
        </div>
      </button>

      {selected && (
        <RowControls
          env={env}
          app={app}
          chatbots={chatbots}
          onSelectChatbot={onSelectChatbot}
          onApply={onApply}
        />
      )}
    </div>
  );
}

function DatabaseRowHeader({ env, current }: { env: Environment; current: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {current && <Check className="h-4 w-4 shrink-0 text-green-500" />}
      <span className="truncate font-medium">{env.name}</span>
      {current && <span className="text-xs text-muted-foreground">(current)</span>}
      {env.branch.prNumber ? (
        <span className="text-xs text-muted-foreground">PR #{env.branch.prNumber}</span>
      ) : null}
    </div>
  );
}

/**
 * The chatbot axis, on the selected row only — the second dropdown of a two-axis choice
 * repeated down every row would be noise, and the pair is applied from here anyway.
 *
 * Every chatbot is offered for every database. A pinned one on the wrong database is
 * flagged rather than withheld: a combination nobody offers is a combination nobody can
 * test, and the flag says exactly what is wrong with it.
 */
function RowControls({
  env,
  app,
  chatbots,
  onSelectChatbot,
  onApply,
}: {
  env: Environment;
  app: AppWiring;
  chatbots: ChatbotTarget[];
  onSelectChatbot: (url: string) => void;
  onApply: (env: Environment) => void;
}) {
  const blocker = environmentBlocker(env);
  const mismatch = pairingMismatch(env.chatbot, env.ref, app.routesPerToken);

  return (
    <div className="mt-2 space-y-2 border-t pt-2">
      <div className="flex items-center gap-2">
        <label htmlFor="picker-chatbot" className="shrink-0 text-xs text-muted-foreground">
          Chatbot
        </label>
        <select
          id="picker-chatbot"
          value={env.chatbot.url}
          onChange={(event) => onSelectChatbot(event.target.value)}
          className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs"
        >
          {chatbots.map((option) => (
            <option key={option.url} value={option.url}>
              {chatbotLabel(option)}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          onClick={() => onApply(env)}
          disabled={blocker !== null || isCurrentEnvironment(env, app.appRef)}
          title={blocker || undefined}
        >
          Switch
        </Button>
      </div>

      {blocker && <p className="text-xs text-destructive">{blocker}</p>}
      {mismatch && <VerdictLine verdict={pairingVerdict(mismatch)} />}
    </div>
  );
}
